import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Users, Search, ShoppingBag, DollarSign, Mail, Plus, Instagram, Globe, MapPin, Phone, Loader2 } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const GlassCard = ({ children, className = '', ...props }) => (
  <div className={`bg-[#171A2C]/60 backdrop-blur-xl border border-white/5 shadow-[0_8px_32px_0_rgba(0,0,0,0.36)] rounded-2xl ${className}`} {...props}>{children}</div>
);

export default function DashboardCustomers() {
  const [customers, setCustomers] = useState([]);
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [saving, setSaving] = useState(false);
  const [addForm, setAddForm] = useState({ name: '', email: '', phone: '', address: '', city: '', state: '', notes: '', instagram: '', facebook: '', twitter: '', website: '' });

  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };

  const fetchCustomers = () => {
    axios.get(`${API}/customers`, { headers }).then(res => setCustomers(res.data || [])).finally(() => setLoading(false));
  };

  useEffect(() => { fetchCustomers(); }, []);

  const handleAddContact = async (e) => {
    e.preventDefault();
    if (!addForm.name && !addForm.email) {
      toast.error('Please enter at least a name or email');
      return;
    }
    setSaving(true);
    try {
      await axios.post(`${API}/customers`, {
        name: addForm.name || 'Unknown',
        email: addForm.email || '',
        phone: addForm.phone || '',
        address: addForm.address || '',
        city: addForm.city || '',
        state: addForm.state || '',
        notes: addForm.notes || '',
        social_links: { instagram: addForm.instagram, facebook: addForm.facebook, twitter: addForm.twitter, website: addForm.website },
        source: 'manual'
      }, { headers });
      toast.success('Contact added');
      setShowAdd(false);
      setAddForm({ name: '', email: '', phone: '', address: '', city: '', state: '', notes: '', instagram: '', facebook: '', twitter: '', website: '' });
      fetchCustomers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add contact');
    } finally {
      setSaving(false);
    }
  };

  const filtered = customers.filter(c =>
    c.name?.toLowerCase().includes(search.toLowerCase()) ||
    c.email?.toLowerCase().includes(search.toLowerCase())
  );

  if (loading) {
    return <div className="flex items-center justify-center h-64"><Loader2 size={32} className="animate-spin text-[#8B5CF6]" /></div>;
  }

  return (
    <div className="space-y-6" data-testid="customers-page">
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h1 className="text-2xl font-bold text-white">Customers</h1>
          <p className="text-sm text-[#9CA3AF] mt-1">{customers.length} contacts</p>
        </div>
        <div className="flex items-center gap-3">
          <div className="relative">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#64748B]" />
            <Input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search..." className="pl-9 bg-white/5 border-white/10 text-white placeholder:text-[#64748B] w-56 rounded-xl" data-testid="customer-search" />
          </div>
          <button onClick={() => setShowAdd(true)} className="flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-medium bg-gradient-to-r from-[#8B5CF6] to-[#3B82F6] text-white shadow-[0_0_15px_rgba(139,92,246,0.3)] hover:shadow-[0_0_25px_rgba(139,92,246,0.5)] transition-all" data-testid="add-contact-btn">
            <Plus size={16} /> Add Contact
          </button>
        </div>
      </div>

      <GlassCard className="overflow-hidden">
        <table className="w-full text-left text-sm" data-testid="customers-table">
          <thead>
            <tr className="text-xs uppercase tracking-wider text-[#64748B] border-b border-white/5">
              <th className="px-6 py-3 font-medium">Customer</th>
              <th className="px-6 py-3 font-medium">Total Spent</th>
              <th className="px-6 py-3 font-medium">Orders</th>
              <th className="px-6 py-3 font-medium">Source</th>
              <th className="px-6 py-3 font-medium">Actions</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((customer, i) => (
              <tr key={i} className="border-b border-white/5 hover:bg-white/[0.02] transition-colors" data-testid={`customer-row-${i}`}>
                <td className="px-6 py-4">
                  <div className="flex items-center gap-3">
                    <div className="w-9 h-9 rounded-full bg-gradient-to-br from-[#8B5CF6]/30 to-[#3B82F6]/30 flex items-center justify-center text-xs font-bold text-white">
                      {customer.name?.[0]?.toUpperCase() || '?'}
                    </div>
                    <div>
                      <p className="text-white font-medium">{customer.name}</p>
                      <p className="text-[#64748B] text-xs">{customer.email}</p>
                    </div>
                  </div>
                </td>
                <td className="px-6 py-4 text-[#00CC99] font-medium">${(customer.total_spent || 0).toFixed(2)}</td>
                <td className="px-6 py-4 text-[#94A3B8]">{customer.order_count || 0}</td>
                <td className="px-6 py-4">
                  <span className={`text-xs px-2 py-0.5 rounded-full ${customer.source === 'manual' ? 'bg-[#8B5CF6]/10 text-[#A78BFA] border border-[#8B5CF6]/20' : 'bg-white/5 text-[#64748B] border border-white/10'}`}>
                    {customer.source === 'manual' ? 'Manual' : 'Purchase'}
                  </span>
                </td>
                <td className="px-6 py-4">
                  <button onClick={() => setSelected(customer)} className="text-xs text-[#A78BFA] hover:text-white transition-colors" data-testid={`view-customer-${i}`}>View</button>
                </td>
              </tr>
            ))}
            {filtered.length === 0 && (
              <tr><td colSpan={5} className="px-6 py-16 text-center">
                <Users size={48} className="mx-auto text-[#64748B] mb-4" />
                <p className="text-[#9CA3AF]">No customers found</p>
              </td></tr>
            )}
          </tbody>
        </table>
      </GlassCard>

      {/* Add Contact Dialog */}
      <Dialog open={showAdd} onOpenChange={setShowAdd}>
        <DialogContent className="bg-[#1E1E3A] border-white/10 text-white max-w-lg max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="text-lg flex items-center gap-2"><Plus size={18} className="text-[#A78BFA]" /> Add Contact</DialogTitle></DialogHeader>
          <form onSubmit={handleAddContact} className="space-y-4 mt-2" data-testid="add-contact-form">
            <p className="text-xs text-[#9CA3AF]">All fields are optional except name or email.</p>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-[#94A3B8] text-xs">Name</Label><Input value={addForm.name} onChange={e => setAddForm(p => ({...p, name: e.target.value}))} placeholder="John Doe" className="mt-1 bg-white/5 border-white/10 text-white placeholder:text-[#64748B] rounded-xl" data-testid="contact-name" /></div>
              <div><Label className="text-[#94A3B8] text-xs">Email</Label><Input type="email" value={addForm.email} onChange={e => setAddForm(p => ({...p, email: e.target.value}))} placeholder="john@email.com" className="mt-1 bg-white/5 border-white/10 text-white placeholder:text-[#64748B] rounded-xl" data-testid="contact-email" /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-[#94A3B8] text-xs flex items-center gap-1"><Phone size={12} /> Phone</Label><Input value={addForm.phone} onChange={e => setAddForm(p => ({...p, phone: e.target.value}))} placeholder="+1 555-0123" className="mt-1 bg-white/5 border-white/10 text-white placeholder:text-[#64748B] rounded-xl" data-testid="contact-phone" /></div>
              <div><Label className="text-[#94A3B8] text-xs flex items-center gap-1"><MapPin size={12} /> City</Label><Input value={addForm.city} onChange={e => setAddForm(p => ({...p, city: e.target.value}))} placeholder="Toronto" className="mt-1 bg-white/5 border-white/10 text-white placeholder:text-[#64748B] rounded-xl" /></div>
            </div>
            <div><Label className="text-[#94A3B8] text-xs flex items-center gap-1"><MapPin size={12} /> Address</Label><Input value={addForm.address} onChange={e => setAddForm(p => ({...p, address: e.target.value}))} placeholder="123 Main St" className="mt-1 bg-white/5 border-white/10 text-white placeholder:text-[#64748B] rounded-xl" /></div>

            <div className="pt-3 border-t border-white/5">
              <p className="text-xs text-[#64748B] uppercase tracking-wider font-semibold mb-3">Social Media</p>
              <div className="grid grid-cols-2 gap-3">
                <div><Label className="text-[#94A3B8] text-xs flex items-center gap-1"><Instagram size={12} /> Instagram</Label><Input value={addForm.instagram} onChange={e => setAddForm(p => ({...p, instagram: e.target.value}))} placeholder="@handle" className="mt-1 bg-white/5 border-white/10 text-white placeholder:text-[#64748B] rounded-xl" data-testid="contact-instagram" /></div>
                <div><Label className="text-[#94A3B8] text-xs flex items-center gap-1"><Globe size={12} /> Website</Label><Input value={addForm.website} onChange={e => setAddForm(p => ({...p, website: e.target.value}))} placeholder="https://..." className="mt-1 bg-white/5 border-white/10 text-white placeholder:text-[#64748B] rounded-xl" /></div>
              </div>
            </div>

            <div><Label className="text-[#94A3B8] text-xs">Notes</Label><Textarea value={addForm.notes} onChange={e => setAddForm(p => ({...p, notes: e.target.value}))} placeholder="Any notes about this contact..." rows={2} className="mt-1 bg-white/5 border-white/10 text-white placeholder:text-[#64748B] rounded-xl" data-testid="contact-notes" /></div>

            <div className="flex gap-3 pt-2">
              <button type="button" onClick={() => setShowAdd(false)} className="flex-1 py-2.5 rounded-xl border border-white/10 text-[#9CA3AF] hover:bg-white/5 transition-colors text-sm">Cancel</button>
              <button type="submit" disabled={saving} className="flex-1 py-2.5 rounded-xl font-medium text-white bg-gradient-to-r from-[#8B5CF6] to-[#3B82F6] shadow-[0_0_15px_rgba(139,92,246,0.3)] disabled:opacity-50 transition-all text-sm" data-testid="save-contact-btn">{saving ? 'Saving...' : 'Add Contact'}</button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Customer Detail Dialog */}
      <Dialog open={!!selected} onOpenChange={() => setSelected(null)}>
        <DialogContent className="bg-[#1E1E3A] border-white/10 text-white max-w-md">
          <DialogHeader><DialogTitle className="text-lg flex items-center gap-2"><Users size={18} className="text-[#00FFFF]" /> Customer Details</DialogTitle></DialogHeader>
          {selected && (
            <div className="space-y-4" data-testid="customer-detail">
              <div className="flex items-center gap-4 p-4 bg-white/5 rounded-xl">
                <div className="w-12 h-12 rounded-full bg-gradient-to-br from-[#8B5CF6]/30 to-[#3B82F6]/30 flex items-center justify-center text-lg font-bold">{selected.name?.[0]?.toUpperCase()}</div>
                <div>
                  <p className="text-white font-semibold">{selected.name}</p>
                  <p className="text-sm text-[#9CA3AF] flex items-center gap-1"><Mail size={12} /> {selected.email || 'No email'}</p>
                  {selected.phone && <p className="text-xs text-[#64748B] flex items-center gap-1"><Phone size={12} /> {selected.phone}</p>}
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="p-3 bg-white/5 rounded-xl text-center"><DollarSign size={16} className="mx-auto mb-1 text-[#00CC99]" /><p className="text-lg font-bold text-white">${(selected.total_spent || 0).toFixed(2)}</p><p className="text-xs text-[#64748B]">Total Spent</p></div>
                <div className="p-3 bg-white/5 rounded-xl text-center"><ShoppingBag size={16} className="mx-auto mb-1 text-[#0075FF]" /><p className="text-lg font-bold text-white">{selected.order_count || 0}</p><p className="text-xs text-[#64748B]">Orders</p></div>
              </div>
              {(selected.address || selected.city) && (
                <div className="p-3 bg-white/5 rounded-xl text-sm"><p className="text-xs text-[#64748B] mb-1">Address</p><p className="text-[#94A3B8]">{selected.address}{selected.city ? `, ${selected.city}` : ''}{selected.state ? `, ${selected.state}` : ''}</p></div>
              )}
              {selected.social_links && Object.values(selected.social_links).some(v => v) && (
                <div className="p-3 bg-white/5 rounded-xl text-sm"><p className="text-xs text-[#64748B] mb-2">Social</p>
                  <div className="space-y-1">
                    {selected.social_links.instagram && <p className="text-[#A78BFA] text-xs flex items-center gap-1"><Instagram size={12} /> {selected.social_links.instagram}</p>}
                    {selected.social_links.website && <p className="text-[#A78BFA] text-xs flex items-center gap-1"><Globe size={12} /> {selected.social_links.website}</p>}
                  </div>
                </div>
              )}
              {selected.notes && <div className="p-3 bg-white/5 rounded-xl text-sm"><p className="text-xs text-[#64748B] mb-1">Notes</p><p className="text-[#94A3B8]">{selected.notes}</p></div>}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
